import * as React from 'react';
import { createContext, useContext, ReactElement, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp, faSort, faFilter, faAngleLeft, faAngleDoubleLeft, faAngleRight, faAngleDoubleRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import * as classNames from 'classnames';
import { Direction, useQuery } from './Query';
import { GetPropByString } from '../misc/Helpers/StringHelper';

const RowContext = createContext<any>({});

export const useRow = () => useContext(RowContext);

interface TableProps<T> {
    rowKeyField?: keyof(T);
    data: T[];
    automationId?: string;
    children?: ReactElement<ColumnProps>[];
}


export const Table = <T extends { id?: string }> ({ rowKeyField, data, automationId, children = [] }: TableProps<T>) => {
    let rowKeyFieldValue = rowKeyField ?? "id" as keyof (T);
    const { refreshing, count } = useQuery();

    const headers = children.filter(c => c !== null && c?.props).map((child, index) => (
        <th key={`header${index}`} className="text-nowrap" data-automationid={`header${index}`}>{child.props.header}</th>
    ));

    const rows = data.map((value, index) => (
        <RowContext.Provider key={`row${GetPropByString(value, rowKeyFieldValue)}`} value={value}>
            <tr data-automationid={`row${index}`} data-id={value.id}>{children.filter(c => c?.props).map((child, index) => <td key={`column${child.props.field}${index}`} className={child.props.wrapText ? "" : "text-nowrap"} data-automationid={`column${index}`}>{child}</td>)}</tr>
        </RowContext.Provider>
    ));

    const refreshBody = refreshing && count < 1
        ? (
            <tbody data-automationid="querying">
                <tr>
                    <td colSpan={children.filter(c => c?.props).length}>
                        <p className="text-center">Querying for data</p>
                    </td>
                </tr>
            </tbody>
        )
        : null;

    const emptyBody = !refreshing && count < 1
        ? (
            <tbody data-automationid="loaded">
                <tr>
                    <td colSpan={children.filter(c => c?.props).length}>
                        <p className="text-center">No data available</p>
                    </td>
                </tr>
            </tbody>
        )
        : null;

    const body = count > 0
        ? (
            <tbody data-automationid={`${refreshing ? 'loading' : 'loaded'}`} className={`${refreshing ? 'text-muted' : ''}`}>{rows}</tbody>
        )
        : null;

    return (
        <div className="table-responsive" style={{ overflowX: "auto", overflowY: "visible" }}>
                <table style={{ marginBottom: "48px" }} className="table table-striped table-bordered table-hover" data-automationid={automationId}>
                    <thead>
                        <tr>{headers}</tr>
                    </thead>
                    {refreshBody}
                    {emptyBody}
                    {body}
                    </table>
            <TablePagination />
        </div>
    );
}

export interface ColumnProps {
    field?: string;
    header?: ReactNode;
    wrapText?: boolean;
    formatter?: (value: any) => ReactNode;
    checkBlinded?: boolean;
    analysisTypeField?: string;
}

export const Column = ({ field, formatter = (value) => value, checkBlinded, analysisTypeField = "type" }: ColumnProps) => {
    const row = useRow();
    const value = field
        ? row[field]
        : null;
    let formatted = field ? formatter(value) : formatter(row);
    if (checkBlinded) {
        let analysisType = row[analysisTypeField] as string;
        formatted = analysisType.includes("QC") ? "-" : formatted;
    }  

    return (
        <>{formatted}</>
    );
}

interface FilterHeaderProps {
    sorted?: Direction;
    filtered?: boolean;
    onSort?: () => void;
    onFilter?: () => void;
    children?: ReactNode;
    filterable?: boolean;
    automationId?: string;
    sortEnabled?: boolean;
}

export const FilterHeader = ({ sorted = null, filtered = false, onSort, onFilter, children, filterable = true, automationId, sortEnabled = true}: FilterHeaderProps) => {

    const sortIcon = sorted === null
        ? faSort
        : sorted === 'desc'
            ? faSortDown
            : faSortUp;

    const sortedClass = sorted !== null
        ? 'text-info'
        : '';

    const filteredClass = filtered
        ? 'text-info'
        : '';

    const sort = sortEnabled && (sorted || onSort)
        ? <a className={sortedClass} style={{ display: "inline-block", paddingLeft: "8px" }} data-automationid={`${automationId}_Sort`} onClick={onSort}><FontAwesomeIcon icon={sortIcon} /></a>
        : null;

    const filter = filtered || onFilter
        ? <a className={filteredClass} style={{ display: "inline-block", paddingLeft: "8px" }} data-automationid={`${automationId}_Filter`} onClick={onFilter}><FontAwesomeIcon icon={faFilter} /></a>
        : null;

    return (
        <div className="query-header">
            {children}
            {sortEnabled ? sort : null}
            {filter}
        </div>
    );
}

const TablePagination = () => {
    const { count, paging, applyPaging } = useQuery();
    const { page, size } = paging;
    const pageTotal = Math.ceil(count / size);
    const firstPage = 1;
    const lastPage = pageTotal || 1;
    const jump = 2;
    let previousPage = page - 1;
    let nextPage = page + 1;
    let start = page - jump;
    let end = page + jump;
    const pages = [];
    const changePage = (page: number) => applyPaging({...paging, page: page})

    if (start < firstPage)
        end += start * -1 + 1;

    if (end > lastPage)
        start -= end - lastPage;

    if (start < firstPage)
        start = firstPage;

    if (end > lastPage)
        end = lastPage;

    if (previousPage < firstPage)
        previousPage = firstPage;

    if (nextPage > lastPage)
        nextPage = lastPage;

    for (let p = start; p <= end; p++) {
        const pageItemClasses = classNames("page-item", { active: p === page });

        pages.push(
            <li key={`PageLink${p}`} className={pageItemClasses}>
                <a className="page-link" onClick={() => changePage(p)} href="#">
                    {p === start && p !== firstPage
                        ? "..."
                        : p === end && p !== lastPage
                            ? "..."
                            : p}
                </a>
            </li>
        );
    }

    const firstPageDisabled = page === firstPage;
    const firstPageItemClasses = classNames("page-item", { disabled: firstPageDisabled });

    const lastPageDisabled = page === lastPage;
    const lastPageItemClasses = classNames("page-item", { disabled: lastPageDisabled });

    return (
        <nav aria-label="Table pages">
            <ul className="pagination">
                <li className={firstPageItemClasses}>
                    <a className="page-link" onClick={() => changePage(firstPage)} aria-label="First Page" href={firstPageDisabled ? null : "#"}>
                        <span aria-hidden="true">
                            <FontAwesomeIcon icon={faAngleDoubleLeft} />
                        </span>
                        <span className="sr-only">First</span>
                    </a>
                </li>
                <li className={firstPageItemClasses}>
                    <a className="page-link" onClick={() => changePage(previousPage)} aria-label="Previous Page" href={firstPageDisabled ? null : "#"}>
                        <span aria-hidden="true">
                            <FontAwesomeIcon icon={faAngleLeft} />
                        </span>
                        <span className="sr-only">Previous</span>
                    </a>
                </li>
                {pages}
                <li className={lastPageItemClasses}>
                    <a className="page-link" onClick={() => changePage(nextPage)} aria-label="Next Page" href={lastPageDisabled ? null : "#"}>
                        <span aria-hidden="true">
                            <FontAwesomeIcon icon={faAngleRight} />
                        </span>
                        <span className="sr-only">Next</span>
                    </a>
                </li>
                <li className={lastPageItemClasses}>
                    <a className="page-link" onClick={() => changePage(lastPage)} aria-label="Last Page" href={lastPageDisabled ? null : "#"}>
                        <span aria-hidden="true">
                            <FontAwesomeIcon icon={faAngleDoubleRight} />
                        </span>
                        <span className="sr-only">Last</span>
                    </a>
                </li>
            </ul>
        </nav>
    );
}
